import type { Pipeline, PipelineAudienceType, PipelinesResponse } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  createPipeline,
  createPipelineStage,
  deletePipeline,
  deletePipelineStage,
  favoritePipeline,
  reorderPipelines,
  unfavoritePipeline,
  updatePipeline,
  updatePipelineStage,
  type CreatePipelineStageParams,
  type DeletePipelineStageParams,
  type ReorderPipelinesParams,
  type UpdatePipelinePayload,
  type UpdatePipelineStageParams,
} from "./api"

export interface CreatePipelineParams {
  book_ids?: string[]
  name: string
  audience_types: PipelineAudienceType[]
  household_type?: "prospect" | "client" | null
}

export interface CreatePipelineMutationArgs {
  payload: CreatePipelineParams
  scope?: "company"
}

export function useCreatePipeline() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation<Pipeline, Error, CreatePipelineMutationArgs>({
    mutationFn: async ({ payload, scope }) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return createPipeline(context, payload, scope ? { scope } : undefined)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipelines"] })
    },
  })
}

export interface UpdatePipelineParams extends UpdatePipelinePayload {
  id: string
  scope?: "company"
}

export function useUpdatePipeline() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation<Pipeline, Error, UpdatePipelineParams>({
    mutationFn: async ({ id, scope, ...params }) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return updatePipeline(context, id, params, scope ? { scope } : undefined)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["pipelines"] })
      queryClient.invalidateQueries({ queryKey: ["pipeline", data.id] })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("opportunities"),
      })
    },
  })
}

export interface DeletePipelineArgs {
  pipelineId: string
  replacementDefaultPipelineId?: string
}

export function useDeletePipeline() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation<{ message: string }, Error, DeletePipelineArgs>({
    mutationFn: async ({ pipelineId, replacementDefaultPipelineId }) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return deletePipeline(context, pipelineId, replacementDefaultPipelineId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipelines"] })
      queryClient.invalidateQueries({ queryKey: ["opportunities"] })
    },
  })
}

export function useCreatePipelineStage(pipelineId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreatePipelineStageParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return createPipelineStage(context, pipelineId, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipeline"] })
    },
  })
}

export function useUpdatePipelineStage() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async (params: UpdatePipelineStageParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return updatePipelineStage(context, params)
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ["pipeline", params.pipelineId] })

      const previousPipeline = queryClient.getQueryData<Pipeline>(["pipeline", params.pipelineId])

      if (previousPipeline) {
        queryClient.setQueryData<Pipeline>(["pipeline", params.pipelineId], (old) => {
          if (!old) return old
          return {
            ...old,
            pipeline_stages: old.pipeline_stages.map((stage) =>
              stage.id === params.stageId ? { ...stage, ...params.updates } : stage
            ),
          }
        })
      }

      return { previousPipeline }
    },
    onError: (_, params, context) => {
      if (context?.previousPipeline) {
        queryClient.setQueryData(["pipeline", params.pipelineId], context.previousPipeline)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["pipeline"] })
    },
  })
}

export function useMovePipelineStage() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async (params: UpdatePipelineStageParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return updatePipelineStage(context, params)
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ["pipeline", params.pipelineId] })

      const previousPipeline = queryClient.getQueryData<Pipeline>(["pipeline", params.pipelineId])

      if (previousPipeline) {
        queryClient.setQueryData<Pipeline>(["pipeline", params.pipelineId], (old) => {
          if (!old) return old

          const stages = [...old.pipeline_stages]
          const changingStage = stages.find((s) => s.id === params.stageId)
          const currentStageAtStageOrder = stages.find((s) => s.stage_order === params.updates.stage_order)
          const newStages = stages.map((s) => {
            if (s.id === params.stageId) {
              return { ...s, ...params.updates }
            } else if (s.id === currentStageAtStageOrder?.id && changingStage) {
              return { ...s, stage_order: changingStage.stage_order }
            } else {
              return s
            }
          })

          return {
            ...old,
            pipeline_stages: newStages,
          }
        })
      }

      return { previousPipeline }
    },
    onError: (_, params, context) => {
      if (context?.previousPipeline) {
        queryClient.setQueryData(["pipeline", params.pipelineId], context.previousPipeline)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["pipeline"] })
    },
  })
}

export function useDeletePipelineStage() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async (params: DeletePipelineStageParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return deletePipelineStage(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipeline"] })
    },
  })
}

export interface ReorderPipelinesMutationArgs {
  params: ReorderPipelinesParams
  scope?: "company"
}

export function useReorderPipelines() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation<void, Error, ReorderPipelinesMutationArgs>({
    mutationFn: async ({ params, scope }) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return reorderPipelines(context, params, scope ? { scope } : undefined)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipelines"] })
    },
  })
}

export interface TogglePipelineFavoriteArgs {
  pipelineId: string
  favorite: boolean
}

export function useTogglePipelineFavorite() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation<
    void,
    Error,
    TogglePipelineFavoriteArgs,
    { snapshots: [readonly unknown[], PipelinesResponse | undefined][] }
  >({
    mutationFn: async ({ pipelineId, favorite }) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return favorite ? favoritePipeline(context, pipelineId) : unfavoritePipeline(context, pipelineId)
    },
    onMutate: async ({ pipelineId, favorite }) => {
      await queryClient.cancelQueries({ queryKey: ["pipelines"] })

      const snapshots = queryClient.getQueriesData<PipelinesResponse>({ queryKey: ["pipelines"] })

      snapshots.forEach(([key, data]) => {
        if (!data) return
        const ids = data.favorite_pipeline_ids
        const next = favorite
          ? ids.includes(pipelineId)
            ? ids
            : [...ids, pipelineId]
          : ids.filter((id) => id !== pipelineId)
        queryClient.setQueryData<PipelinesResponse>(key, { ...data, favorite_pipeline_ids: next })
      })

      return { snapshots }
    },
    onError: (_error, _variables, context) => {
      context?.snapshots.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["pipelines"] })
      queryClient.invalidateQueries({ queryKey: ["pipeline"] })
    },
  })
}
